/**
 * `exitIsolatingBlockDown(blockName)` — TipTap command factory for the
 * ArrowDown / Mod-Enter keyboard shortcut on isolating block-content
 * nodes (panels, expand-section).
 *
 * Default ProseMirror behavior when the user presses ArrowDown from
 * inside an isolating block whose last paragraph holds the cursor:
 *
 *   1. ArrowDown moves the cursor to the next visible line.
 *   2. There IS no next visible line inside the block — the block is
 *      `isolating: true` so the cursor can't leak past its boundary.
 *   3. The next ArrowDown press lands on ProseMirror's gap cursor,
 *      which renders as a thin horizontal line below the block ("the
 *      blinking underline").
 *
 * Users unfamiliar with the gap-cursor convention read this as a UX
 * bug. The fix this command applies: detect "cursor at end of last
 * child of the block" and explicitly exit into a fresh paragraph
 * (creating one if the block has no following sibling). The cursor
 * lands at offset 0 of the new paragraph — a normal text caret, no
 * gap-cursor flash.
 *
 * Returns a TipTap command (NOT a configured Command object) so it
 * can plug into `addKeyboardShortcuts() { return { 'ArrowDown': ... } }`
 * on any extension that wants the behavior.
 *
 * Matches Notion / Confluence exit-block-down semantics.
 */

import { TextSelection } from '@tiptap/pm/state'
import type { CommandProps } from '@tiptap/react'

/**
 * Returns true (command handled) only when the cursor was at the end
 * of the named block's last child and an exit was performed. Returns
 * false in every other case so the default ArrowDown behavior takes
 * over (move-by-line within the block, or move into a following
 * sibling when one already exists in the parent flow).
 */
export function exitIsolatingBlockDown(blockName: string) {
  return ({ state, dispatch }: CommandProps): boolean => {
    const { $from, empty } = state.selection
    // Only fire on a collapsed selection — otherwise this would
    // collapse-and-exit on Shift-arrow range selections.
    if (!empty) return false

    // Find the block ancestor at this position. Walk from the cursor
    // depth up; bail if not inside the named block.
    let blockDepth = -1
    for (let d = $from.depth; d > 0; d--) {
      if ($from.node(d).type.name === blockName) {
        blockDepth = d
        break
      }
    }
    if (blockDepth === -1) return false

    // Cursor must be at the end of its current textblock parent.
    if ($from.parentOffset !== $from.parent.content.size) return false

    // Cursor's textblock must be the LAST child of the block — if
    // there's more content below within the block, default behavior
    // (move down to the next line inside) is correct.
    const block = $from.node(blockDepth)
    const indexInBlock = $from.index(blockDepth)
    if (indexInBlock !== block.childCount - 1) return false

    // Position of the close token of the block + 1 = position right
    // after the block in the parent flow.
    const afterBlock = $from.after(blockDepth)

    // Is there already a sibling node after the block?
    const parent = $from.node(blockDepth - 1)
    const blockIndex = $from.index(blockDepth - 1)
    const hasNextSibling = blockIndex + 1 < parent.childCount

    if (hasNextSibling) {
      // Move cursor to the start of the existing next sibling. Avoids
      // the gap cursor entirely by jumping past the boundary.
      if (!dispatch) return true
      const nextPos = afterBlock + 1
      dispatch(state.tr.setSelection(TextSelection.create(state.doc, nextPos)).scrollIntoView())
      return true
    }

    // No sibling — insert a paragraph after the block and move the
    // cursor into it. Falls back to false if the schema doesn't
    // include `paragraph` (defensive; every wiki schema does).
    const paragraphType = state.schema.nodes.paragraph
    if (!paragraphType) return false
    if (!dispatch) return true
    const para = paragraphType.create()
    const tr = state.tr.insert(afterBlock, para)
    tr.setSelection(TextSelection.create(tr.doc, afterBlock + 1))
    dispatch(tr.scrollIntoView())
    return true
  }
}
